import { useEffect, useRef, useState } from 'react'
import useWindowSize from '@/lib/useWindowSize'
import Portal from '@/utils/Portal'
import { useTheme } from 'next-themes'
import { useDirectionContext } from '@/components/common/DirectionSelect'

// Tooltip is placed on top of everything (including top menu and bottom status bar).
// @@TODO: Prevent tooltip from being displayed off-screen (clipped). Also shift callout accordingly.
// @@TODO: When switching light/dark the scrollbars are (sometimes) still in the old color. Force rerender?

type Coords = {
  left: number
  top: number
  width: number
  height: number
}

export const TextBalloon = ({ message }: { message: string }) => {
  /*
   ** Tooltip has a downward-pointing triangle at the bottom as callout.
   */
  const triangle = (
    <div>
      <svg height="10.0" width="14.2" fill="currentColor">
        <polygon points="0,0 14.2,0 7.1,9.0" />
      </svg>
    </div>
  )

  return (
    <div className="w-fit justify-center">
      <div
        role="alert"
        className="w-fit items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700"
      >
        <div className="rounded-md border-2 bg-gray-200 dark:bg-gray-800 border-gray-100 dark:border-gray-800">
          <div className="w-fit max-w-50 h-fit max-h-25 overflow-auto bg-gray-100 dark:bg-gray-800">
            <div className="p-2 text-center text-sm font-sans text-black dark:text-white">
              {message}
            </div>
          </div>
        </div>
      </div>
      <div className="text-gray-300 dark:text-gray-700 flex justify-center">
        {triangle}
      </div>
    </div>
  )
}

const Tooltip = ({
  children,
  message,
}: {
  children: React.ReactNode
  message: string
}) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<Coords>({
    left: -999,
    top: -999,
    width: -999,
    height: -999,
  })
  // Location and/or appearance of tooltip is altered by:
  // - changing size of window;
  // - changing theme;
  // - changing direction: ltr <-> rtl.
  const { width, height } = useWindowSize()
  const { theme } = useTheme()
  const { direction } = useDirectionContext()
  const [hover, setHover] = useState(false)
  const [scroll, setScroll] = useState(false)

  const contents = (
    <div ref={contentsRef}>
      <TextBalloon message={message} />
    </div>
  )

  const handlerRef = useRef<(() => void) | null>(null)

  useEffect(
    function setupListeners() {
      handlerRef.current = () => {
        setScroll((prev) => !prev)
      }

      const listener = () => {
        handlerRef.current?.()
      }

      // Prevent adding unlimited event listeners (due to hot reload in dev mode).
      document.addEventListener('scroll', listener, {
        capture: true,
        passive: true,
      })
      if (anchorRef.current && contentsRef.current) {
        const anchorRect = anchorRef?.current?.getBoundingClientRect()
        const contentsRect = contentsRef.current.getBoundingClientRect()
        if (anchorRect && contentsRect) {
          setCoords({
            // 1. Bottom of tooltip touches top of anchor.
            // 2. Tooltip is horizontally centered on center axis of anchor.
            // @@TODO: Tooltip should shift inward if close to left or right border of window.
            left:
              anchorRect.x +
              window.scrollX +
              anchorRect.width / 2 -
              contentsRect.width / 2,
            // NOTE: Shift down 1 pixel to prevent gap between anchor and tooltip (continuous hover).
            top: anchorRect.y + window.scrollY - contentsRect.height + 1,
            width: contentsRect.width,
            height: contentsRect.height,
          })
        }
      }
      return function cleanupListeners() {
        document.removeEventListener('scroll', listener, { capture: true })
      }
    },
    [anchorRef, contentsRef, width, height, theme, direction],
  )

  return (
    <div>
      <div
        role="presentation" // @@TODO: Verify role
        ref={anchorRef}
        className="relative flex flex-col w-fit hover:cursor-pointer"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {children}
        <Portal>
          {/* NOTE: Tooltip should appear on top of everything: z-50. */}
          <div
            role="tooltip"
            className={`${
              hover ? 'opacity-100' : 'opacity-0'
            } z-50 absolute flex flex-col`}
            style={{
              // Use -999 to move off-screen and prevent overlapping.
              left: `${hover ? coords.left : '-999'}px`,
              top: `${coords.top}px`,
              width: `${coords.width}px`,
              height: `${coords.height}px`,
            }}
          >
            {contents}
          </div>
        </Portal>
      </div>
    </div>
  )
}

export default Tooltip
